
import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FAQItem } from '@/components/faq-serp/types';
import { Copy, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface FaqItemProps {
  faq: FAQItem;
  category: "beginner" | "intermediate" | "expert" | "company";
}

const FaqItem: React.FC<FaqItemProps> = ({ faq, category }) => {
  const copyFaqToClipboard = () => {
    const formattedAnswer = `
${faq.answer.intro}
${faq.answer.details.join('\n')}
${faq.answer.conclusion}
`;
    
    navigator.clipboard.writeText(`Q: ${faq.question}\n\nA: ${formattedAnswer.trim()}`);
    toast.success("FAQ copied to clipboard");
  };

  // Different color schemes based on category
  const getCategoryStyles = () => {
    switch (category) {
      case "beginner":
        return {
          border: "border-purple-500/30",
          bg: "bg-gradient-to-r from-purple-500/5 to-indigo-500/5",
          hover: "hover:from-purple-500/10 hover:to-indigo-500/10",
          icon: "text-purple-400",
          title: "from-purple-600 to-indigo-600"
        };
      case "intermediate":
        return {
          border: "border-blue-500/30",
          bg: "bg-gradient-to-r from-blue-500/5 to-cyan-500/5",
          hover: "hover:from-blue-500/10 hover:to-cyan-500/10",
          icon: "text-blue-400",
          title: "from-blue-600 to-cyan-600"
        };
      case "expert":
        return {
          border: "border-teal-500/30",
          bg: "bg-gradient-to-r from-teal-500/5 to-emerald-500/5",
          hover: "hover:from-teal-500/10 hover:to-emerald-500/10",
          icon: "text-teal-400",
          title: "from-teal-600 to-emerald-600"
        };
      case "company":
        return {
          border: "border-amber-500/30",
          bg: "bg-gradient-to-r from-amber-500/5 to-orange-500/5",
          hover: "hover:from-amber-500/10 hover:to-orange-500/10",
          icon: "text-amber-400",
          title: "from-amber-600 to-orange-600"
        };
    }
  };

  const styles = getCategoryStyles();

  return (
    <Accordion 
      type="single" 
      collapsible 
      className={`${styles.bg} backdrop-blur-sm border ${styles.border} rounded-lg shadow-sm transition-all hover:shadow-md ${styles.hover}`}
    >
      <AccordionItem value="item-1" className="border-none">
        <AccordionTrigger className="px-4 py-3 text-left hover:no-underline hover:bg-background/40 rounded-t-lg font-medium group">
          <div className="flex items-center gap-2">
            <HelpCircle className={`h-4 w-4 ${styles.icon} shrink-0 group-hover:text-foreground/100`} />
            <span className={`bg-gradient-to-r ${styles.title} bg-clip-text text-transparent group-hover:opacity-80`}>{faq.question}</span>
          </div>
          
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={(e) => {
              e.stopPropagation();
              copyFaqToClipboard();
            }}
            className="h-8 w-8 ml-2 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Copy FAQ"
          >
            <Copy className="h-4 w-4" />
          </Button>
        </AccordionTrigger>
        <AccordionContent className="p-5 pt-3 text-sm space-y-4 animate-in slide-in-from-top-2 duration-200">
          <p className="font-medium text-base">{faq.answer.intro}</p>
          
          <ul className="space-y-3 pl-1">
            {faq.answer.details.map((detail, i) => (
              <li key={i} className="flex gap-2 text-muted-foreground">
                <span className={styles.icon}>•</span>
                <span>{detail.replace('- ', '')}</span>
              </li>
            ))}
          </ul>
          
          <p className="text-muted-foreground pt-1 italic">{faq.answer.conclusion}</p>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default FaqItem;
